import { MeepOrderRepository } from "Infrastructure/repositories/api/OrderRepository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { OrderRepositoryLocalStorage } from "Infrastructure/repositories/localStorage/OrderRepositoryLocalStorage";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  PrinterStore,
  usePrinterStore,
} from "application/contexts/printer/PrinterStore";
import { useSession } from "application/contexts/session/SessionContext";
import { useUi } from "application/contexts/ui/UIContext";
import { appendCustomerFieldsToObservation } from "modules/order/application/customerFieldsObservation";
import { ErrorCodeType, IError } from "application/models/IError";
import { IActionModalHandler } from "components/actionModal/ActionModal";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { NfceService } from "domains/order/services/NfceService";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import {
  PixApprovedTransaction,
  PixPendingTransaction,
} from "modules/payment/domain/models/PixApprovedTransaction";
import {
  IPaymentRef,
  IPaymentResult,
} from "modules/payment/presentation/PaymentProvider";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { GetError } from "utils/GetError";
import { useShallow } from "zustand/react/shallow";
import { CreatePrintNfceUseCase } from "../application/useCases/CreatePrintNfceUseCase";
import { CreatePrintOrderUseCase } from "../application/useCases/CreatePrintOrderUseCase";
import { CreatePrintReceiptUseCase } from "../application/useCases/CreatePrintReceiptUseCase";
import { CreatePrintRemoteUseCase } from "../application/useCases/CreatePrintRemoteUseCase";
import { shouldEmitNfce } from "../application/useCases/shouldEmitNfce";
import { EmitNfceUseCase } from "../application/useCases/EmitNfceUseCase";
import { GetPasswordUseCase } from "../application/useCases/GetPasswordUseCase";
import { SyncOrderUseCase } from "../application/useCases/SyncOrderUseCase";
import { IEmitedNfce } from "../domain/models/INfce";
import { IOrder } from "../domain/models/IOrder";
import { IOrderLog } from "../domain/models/IOrderLog";
import { IPedidoPosDTO, StatusPedido } from "../domain/models/IPedidoPosDTO";
import {
  IFinancialTransactionModel,
  Payment,
  TransactionType,
} from "../domain/models/Payment";
import { IOrderProviderRef } from "./OrderProvider";
import { ICustomerIdentificationHandler } from "./customerIdentification/CustomerIdentification";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { IPasswordPainel } from "Infrastructure/interfaces/IPasswordPainel";
import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { validateCNPJ, validateCPF } from "utils/utils";
import { useTranslation } from "i18n/useTranslation";
import { ICustomerDiscountsHandler } from "./customerDiscounts/CustomerDiscounts";
import { HttpRepository } from "Infrastructure/repositories/api/_HttpRepository";
import UserRegisterService from "modules/cpfRegister/service/UserRegisterService";
import { GetDocumentOwnerUseCase } from "../application/useCases/GetDocumentOwnerUseCase";
import { isQueueablePix } from "modules/payment/application/pix/pendingPixPolicy";
import { runScreenPixSync } from "./pixQueueSync";
import { pixCancelToastMessage } from "./pixCancelToast";

export type RechargeType =
  | "Recharge"
  | "Payment"
  | "WalletRecharge"
  | undefined;

const locaStorageOrderRepository = OrderRepositoryLocalStorage();
const nfceService = NfceService();
const cashlessRepository = CashlessRepository();

const UseOrderProvider = (
  orderProviderRef: React.ForwardedRef<IOrderProviderRef>,
  printReceiptRef: React.RefObject<IActionModalHandler>,
  printNfceRef: React.RefObject<IActionModalHandler>,
  customerRef: React.RefObject<ICustomerIdentificationHandler>,
  customerDiscountsRef: React.RefObject<ICustomerDiscountsHandler>
) => {
  const paymentProvider = useRef<IPaymentRef>(null);
  const countPaymentVoucher = useRef(0);
  const paymentReceipt = useRef<Payment>();
  const _order = useRef<IOrder>();

  const [addOrdersToPrintQueue] = usePrinterStore(
    useShallow((state: PrinterStore) => [state.addOrders])
  );

  const [clearCart] = useCartStore((state: ICartContext) => [state.clearCart]);
  const { sessaoIdPos } = useSession();
  const { toastFullscreen, toast, showLoading, hideLoading } = useUi();
  const { kiosk } = useKiosk();
  const { replace } = useHistory();
  const { addLog } = useAppInsights();
  const { cardData, isNominalData } = useCardReader();
  const orderRepository = MeepOrderRepository(addLog);
  const { t } = useTranslation();

  const [rechargePayment, setRechargePayment] =
    useState<RechargeType>(undefined);
  const [documentToNf, setDocumentToNf] = useState("");
  const [includDocument, setIncludDocument] = useState(false);
  const [docError, setDocError] = useState("");
  const [enableNfAction, setEnableNfAction] = useState(false);
  const [documentOwnerName, setDocumentOwnerName] = useState("");
  const [documentLookupLoading, setDocumentLookupLoading] = useState(false);

  const docToNf = useRef<string | null>(null);

  useEffect(() => {
    let active = true;

    const validateAndFetchDocument = async () => {
      setDocumentOwnerName("");
      setDocError("");
      setEnableNfAction(false);

      if (!includDocument) {
        docToNf.current = null;
        setEnableNfAction(true);
        return;
      }

      const cleanedDocument = documentToNf.replace(/\D/g, "");

      if (cleanedDocument.length === 11) {
        if (!validateCPF(cleanedDocument)) {
          setDocError(t("input_cpf_cnpj.error.cpf_invalid"));
          return;
        }
      } else if (cleanedDocument.length === 14) {
        if (!validateCNPJ(cleanedDocument)) {
          setDocError(t("input_cpf_cnpj.error.cnpj_invalid"));
          return;
        }
      } else {
        return;
      }

      docToNf.current = cleanedDocument;

      if (!kiosk?.localId) {
        return;
      }

      setDocumentLookupLoading(true);

      try {
        addLog("order", { message: `Validando documento ${cleanedDocument}` });
        const httpRepository = HttpRepository();
        const userRegisterService = UserRegisterService(httpRepository);
        const documentOwner = await GetDocumentOwnerUseCase(
          userRegisterService,
          cleanedDocument,
          kiosk.localId
        );

        addLog("order", {
          message: `Documento validado ${cleanedDocument}`,
          documentOwner,
        });

        if (!active) {
          return;
        }

        if (documentOwner?.name) {
          setDocumentOwnerName(documentOwner.name.toLowerCase());
        }

        setEnableNfAction(true);
        setDocError("");
      } catch (error) {
        addLog("order", {
          message: `Erro ao validar documento ${cleanedDocument}`,
          error,
        });

        if (!active) {
          return;
        }
        setDocError(t("order.nfce_modal.confirm_document_failed"));
        setEnableNfAction(false);
      } finally {
        if (active) {
          setDocumentLookupLoading(false);
        }
      }
    };

    validateAndFetchDocument();

    return () => {
      active = false;
    };
  }, [documentToNf, includDocument, kiosk, t]);

  const onpressKeyboard = useCallback((key: string) => {
    setDocumentToNf((prev) => prev + key);
  }, []);

  const onPressBack = useCallback(() => {
    setDocumentToNf((prev) => prev.substring(0, prev.length - 1));
  }, []);

  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      pedidoPOS?: IPedidoPosDTO,
      payment?: Payment,
      nfce?: IEmitedNfce[]
    ) => {
      try {
        if (order) {
          let orderLog = {
            ...order,
            id: order.id,
            friendlyId: order.friendlyId,
            cartDate: order.cartDate,
            payment: payment ?? order.payment,
            pedidoPOS: pedidoPOS ?? order.pedidoPOS,
            nfce: nfce ?? order.nfce,
            printOrders: order.printOrders,
            ticketId: order.ticketId,
            printType: order.printType,
            orderItems: order.orderItems,
            totalValue: order.totalValue,
          };
          if (message.includes("Pagamento realizado")) {
            const log = {
              id: order.id,
              friendlyId: order.friendlyId,
              cartDate: order.cartDate,
              payment: order.payment,
              totalWithoutDiscount: order.totalWithoutDiscount,
              totalValue: order.totalValue,
              createdAt: order.createdAt,
              passwordPanelCode: order.passwordPanelCode,
              customer: order.customer,
              paymentPix: order.paymentPix,
              groupDiscountOrder: order.groupDiscountOrder,
            };
            addLog("order", { message, log });
          } else {
            addLog("order", { message, orderLog });
          }
          await locaStorageOrderRepository.add(orderLog, message);
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "OrderProvider",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  const syncOrder = useCallback(
    async (
      order: IOrder,
      transactionType: TransactionType,
      kiosk: Kiosk,
      recharge?: RechargeType
    ) => {
      try {
        showLoading();
        const syncResult = await SyncOrderUseCase(
          orderRepository,
          cashlessRepository,
          order,
          transactionType,
          kiosk,
          recharge
        );
        await addOrderLog(order, "Pedido enviado", syncResult.pedidoPOS);
        orderRepository.syncOrdersAsync(kiosk.localId);
        return syncResult;
      } catch (error) {
        GetError(
          error,
          "syncOrder",
          "UseOrderProvider",
          "Falha ao sincronizar pedido",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addOrderLog, orderRepository, showLoading]
  );

  const printPaymentReceipt = useCallback(async () => {
    countPaymentVoucher.current = countPaymentVoucher.current + 1;
    try {
      if (paymentReceipt.current) {
        if (countPaymentVoucher.current <= 1) {
          addOrdersToPrintQueue(
            CreatePrintReceiptUseCase(paymentReceipt.current)
          );
          countPaymentVoucher.current = 0;
        }
      }
    } catch (error) {
      GetError(
        error,
        "printPaymentReceipt",
        "UseOrderProvider",
        "Falha ao imprimir comprovante",
        ErrorCodeType.GENERAL
      );
    }
  }, [addOrdersToPrintQueue]);

  const handleReceiptPrintModal = useCallback(
    async (kiosk: Kiosk, transactionType: TransactionType) => {
      if (kiosk.printReceipt) {
        if (transactionType !== TransactionType.pospaid) {
          try {
            hideLoading();
            await printReceiptRef.current?.open();
            printPaymentReceipt();
          } catch (error) {
            GetError(
              error,
              "printTicket",
              "UseOrderProvider",
              `${(error as any)?.message ?? "Tempo expirado"}`,
              ErrorCodeType.GENERAL
            );
          }
        }
      }
    },
    [hideLoading, printPaymentReceipt, printReceiptRef]
  );

  const emitNfce = useCallback(
    async (order: IOrder, document?: string) => {
      try {
        addOrderLog(order, "Emitir NF");
        await orderRepository.syncOrdersAsync(kiosk?.localId ?? "");
        const nfce = await EmitNfceUseCase(nfceService, order, document ?? docToNf.current ?? "");
        if (!!nfce.length) {
          await addOrderLog(order, "NF Emitida", undefined, undefined, nfce);
          return nfce;
        } else {
          await addOrderLog(order, "Falha ao emitir NF");
          GetError(
            new Error(t("order.nfce.emit_failed_verbose")),
            "emitNfce",
            "OrderProvider",
            t("order.nfce.emit_failed_verbose"),
            ErrorCodeType.NFCE
          );
        }
      } catch (error) {
        GetError(
          error,
          "emitNfce",
          "UseOrderProvider",
          t("order.nfce.emit_failed_verbose"),
          ErrorCodeType.NFCE
        );
        await addOrderLog(
          order,
          `Falha ao emitir nota fiscal - ${(error as any)?.response?.data?.message &&
          (error as any)?.response?.data?.message
          }`
        );
      }
    },
    [addOrderLog, kiosk, orderRepository, t]
  );

  const printNfce = useCallback(
    async (nfce: IEmitedNfce[]) => {
      try {
        if (nfce) {
          addOrdersToPrintQueue(CreatePrintNfceUseCase(nfce));
        }
      } catch (error) {
        GetError(
          error,
          "printNfce",
          "UseOrderProvider",
          t("order.nfce.print_failed"),
          ErrorCodeType.NFCE
        );
        toast(t("order.nfce.print_failed"), "error");
      }
    },
    [addOrdersToPrintQueue, toast, t]
  );

  const handleNfcePrintModal = useCallback(
    async (order: IOrder) => {
      try {
        try {
          if (kiosk?.mustPrintEletronicInvoice) {
            await printNfceRef.current?.open();
          }
          const documentSnapshot = docToNf.current;
          showLoading();
          const nfs = await emitNfce(order, documentSnapshot ?? undefined);
          if (nfs && nfs.length > 0 && kiosk?.mustPrintEletronicInvoice) {
            await printNfce(nfs);
          } else {
            if (kiosk?.mustPrintEletronicInvoice)
              toast(t("order.nfce.emit_failed_verbose"), "error");
          }
        } catch (_err) {
          emitNfce(order, docToNf.current ?? undefined);
        } finally {
          hideLoading();
          setDocumentToNf("");
          docToNf.current = null;
          setIncludDocument(false);
          setEnableNfAction(true);
          setDocError("");
        }
      } catch (error) {
        GetError(
          error,
          "handleNfcePrintModal",
          "UseOrderProvider",
          `${(error as any)?.message ?? t("order.nfce.emit_failed_verbose")}`,
          ErrorCodeType.NFCE
        );
      }
    },
    [
      emitNfce,
      hideLoading,
      printNfce,
      printNfceRef,
      showLoading,
      toast,
      kiosk,
      t
    ]
  );

  const getPassword = useCallback(
    async (_kiosk: Kiosk) => {
      showLoading();
      return await GetPasswordUseCase(
        orderRepository,
        _kiosk.localId,
        sessaoIdPos ?? ""
      );
    },
    [orderRepository, sessaoIdPos, showLoading]
  );

  const onSucessPayment = useCallback(
    async (
      _kiosk: Kiosk,
      orderWithCustomer: IOrder,
      transactionType: TransactionType,
      payment?: Payment,
      recharge?: RechargeType,
      pedidoPos?: IPedidoPosDTO
    ) => {
      if (orderWithCustomer.customer?.document || includDocument) {
        const newDocument = orderWithCustomer.customer?.document || documentToNf;
        setDocumentToNf(newDocument);
        setIncludDocument(true);
        docToNf.current = newDocument.replace(/\D/g, "") || null;
      } else {
        setIncludDocument(false);
        docToNf.current = null;
      }
      paymentReceipt.current = payment;
      _order.current = orderWithCustomer;
      const paidOrder = {
        ...orderWithCustomer,
        payment,
      };

      // Atribuída dentro do callback do envio: o cast evita o estreitamento para undefined
      let password = undefined as IPasswordPainel | undefined;

      // PIX enfileirável: a fila é atualizada e o dono liberado mesmo se algo lançar até o envio
      const queueablePixId =
        transactionType === TransactionType.pix && isQueueablePix(recharge, orderWithCustomer)
          ? orderWithCustomer.paymentPix?.paymentId
          : undefined;

      const { syncResult, nfceDeferred: pixNfceDeferred } = await runScreenPixSync(
        queueablePixId,
        async () => {
          if (!recharge && _kiosk.mustEnableManagementPassword) {
            password = await getPassword(_kiosk);
          }

          if (
            transactionType !== TransactionType.cashless &&
            transactionType !== TransactionType.pospaid
          ) {
            return syncOrder(
              { ...paidOrder, passwordPanelCode: password?.passwordForPrinting },
              transactionType,
              _kiosk,
              recharge
            );
          }
          return {
            pedidoPOS: pedidoPos,
            success: !!pedidoPos,
          };
        },
        addLog
      );
      await addOrderLog(
        { ...orderWithCustomer, paymentApproved: true },
        "Pagamento realizado",
        syncResult?.pedidoPOS,
        payment
      );

      if (!recharge && syncResult?.pedidoPOS) {
        const printOrders = CreatePrintOrderUseCase(
          syncResult.pedidoPOS,
          _kiosk
        );
        addOrderLog(
          {
            ...paidOrder,
            printType: _kiosk.printType,
            printOrders,
          },
          "Enviado para impressão",
          syncResult.pedidoPOS
        );
        addOrdersToPrintQueue(printOrders);
        addOrdersToPrintQueue(
          CreatePrintRemoteUseCase(paidOrder, syncResult.pedidoPOS, _kiosk)
        );
      } else if (!recharge && !syncResult?.pedidoPOS) {
        addLog("print", {
          message: "Print no started: missing pedidoPOS",
          orderLog: { paidOrder },
        });
      }

      if (orderWithCustomer.totalValue > 0) {
        if (
          transactionType !== TransactionType.cashless &&
          transactionType !== TransactionType.pospaid &&
          transactionType !== TransactionType.pix //TODO: Remover ao criar comprovante pix
        ) {
          await handleReceiptPrintModal(_kiosk, transactionType);
        }

        // PIX com envio falho: a NFC-e sai pelo verificador depois do reenvio
        if (shouldEmitNfce(_kiosk, recharge, transactionType) && !pixNfceDeferred) {
          handleNfcePrintModal(paidOrder);
        }
      }

      if (
        recharge === "Recharge" &&
        orderWithCustomer.isOfflineRecharge &&
        payment
      ) {
        addOrdersToPrintQueue(CreatePrintReceiptUseCase(payment));
      }

      if (
        _kiosk.mustEnableManagementPassword &&
        !password?.passwordForPrinting &&
        !recharge
      ) {
        toastFullscreen(
          t("order.toast.no_password_warning"),
          "warning",
          {
            onClose: () => {
              replace("/");
            },
            time: 4000,
          }
        );
      } else if (!recharge) {
        toastFullscreen(
          kiosk?.messageOnPayment ?? t("order.toast.success_default"),
          "success",
          {
            onClose: () => {
              replace("/");
            },
            time: 3000,
          }
        );
      }

      hideLoading();
      clearCart();
    },
    [
      addOrderLog,
      hideLoading,
      clearCart,
      getPassword,
      syncOrder,
      addOrdersToPrintQueue,
      addLog,
      handleReceiptPrintModal,
      handleNfcePrintModal,
      toastFullscreen,
      replace,
      kiosk,
      includDocument,
      documentToNf,
      t
    ]
  );

  const validateCart = useCallback(
    async (_order: IOrder) => {
      if (_order.orderItems.length === 0) {
        await addOrderLog(_order, t("order.errors.cart_empty"));
        throw GetError(
          new Error(t("order.errors.cart_empty")),
          "startPayment",
          "UseOrderProvider",
          t("order.errors.cart_empty"),
          ErrorCodeType.GENERAL
        );
      }
    },
    [addOrderLog, t]
  );

  const getCustomer = useCallback(
    async (_order: IOrder, _kiosk: Kiosk, _recharge?: RechargeType) => {
      const hasItemOnDiscountList = _order.orderItems?.some(
        (item) => item.availableOnDiscountList === true
      );

      if (
        _kiosk?.ignoreCustomerIdentificationWithoutDiscountList &&
        !hasItemOnDiscountList && !_kiosk?.customerFieldEnable &&
        !_kiosk.prism &&
        _kiosk.enableCpfDiscount
      ) {
        return _order;
      }
      if (
        (_kiosk?.customerFieldEnable &&
          !_kiosk.customerFieldDisabled &&
          _kiosk.customerField.length) ||
        _kiosk?.generateEletronicInvoice ||
        _kiosk?.prism ||
        _kiosk?.enableCpfDiscount
      ) {
        const customer = await customerRef.current?.open(_kiosk, _recharge);
        _order = {
          ..._order,
          observation: appendCustomerFieldsToObservation(
            _order.observation,
            customer?.customFields
          ),
          customer: {
            ..._order.customer,
            name: customer?.name ?? _order.customer?.name ?? "",
            phone: customer?.phone ?? _order.customer?.phone ?? "",
            document: customer?.document ?? _order.customer?.document ?? "",
            documentToNf: customer?.documentToNf ?? "",
            email: customer?.email ?? _order.customer?.email ?? "",
            clientIdentificator:
              customer?.clientIdentificator ??
              _order.customer?.clientIdentificator ??
              "",
            prism: customer?.prism ?? _order.customer?.prism ?? "",
          },
        };
      }
      return _order;
    },
    [customerRef]
  );

  const handlePaymentError = useCallback(
    async (error: unknown, order: IOrder): Promise<never> => {
      const iError = Array.isArray(error) ? error[0] : (error as IError);
      if (/pix/i.test(iError?.message ?? "")) {
        try {
          const cancelledPix: PixPendingTransaction = JSON.parse(
            iError.message
          );

          const paymentPix: PixApprovedTransaction = {
            ownerId: cancelledPix?.ownerId ?? "",
            paymentId: cancelledPix?.paymentId ?? "",
            qrCodeLink: cancelledPix?.qrCodeLink ?? "",
            status: cancelledPix?.status ?? "pending",
            value: cancelledPix?.value ?? order.totalValue,
            acquirerType: cancelledPix.acquirerType,
            createdAt: cancelledPix.createdAt,
            endToEndId: cancelledPix.endToEndId,
          };

          await addOrderLog(
            {
              ...order,
              paymentPix,
            },
            cancelledPix?.message ?? `[-003] ${t("order.errors.payment_failed")}`,
            undefined,
            {
              FinancialTransactionModel: {
                Status: StatusPedido.AguardandoPagamento,
              } as IFinancialTransactionModel,
            } as Payment
          );
          // Tempo esgotado / QR cancelado avisam o cliente; fechar pelo usuário não
          const toastMessage = pixCancelToastMessage(cancelledPix?.message);
          if (toastMessage) {
            toast(toastMessage, "error");
          }
        } catch (pixParseError) {
          const message =
            (pixParseError as IError)?.message ?? `[-004] ${t("order.errors.payment_failed")}`;
          await addOrderLog(order, message);
          toast(message, "error");
        } finally {
          throw GetError(
            error,
            "startPayment",
            "UseOrderProvider",
            t("order.errors.payment_failed"),
            ErrorCodeType.PAYMENT
          );
        }
      }

      let errMsg = "";
      try {
        const parsedError = JSON.parse(iError.message);
        errMsg = parsedError?.message ?? "";
      } catch {
        const rawMessage = (error as any)?.response?.data?.message;
        if (typeof rawMessage === "string") {
          const parts = rawMessage.split(" :");
          errMsg = parts.length > 1 ? parts[1] : rawMessage;
        } else {
          errMsg = iError.message;
        }
      }

      await addOrderLog(order, errMsg ?? `[-005] ${t("order.errors.payment_failed")}`);

      if (errMsg && !errMsg.includes("Fechado pelo usuário")) {
        toast(errMsg ?? t("order.errors.payment_failed"), "error");
      }

      throw GetError(
        error,
        "startPayment",
        "UseOrderProvider",
        t("order.errors.payment_failed"),
        ErrorCodeType.PAYMENT
      );
    },
    [addOrderLog, toast, t]
  );

  const startPayment = useCallback(
    async (
      order: IOrder,
      recharge?: RechargeType,
      paymentType?: TransactionType
    ) => {
      if (!kiosk) {
        const payment = {
          success: false,
          errorMessage: t("order.errors.kiosk_config_failed"),
        } as Payment;

        const paymentResult = {
          order,
          payment,
        } as IPaymentResult;

        await addOrderLog(
          order,
          t("order.errors.kiosk_config_failed"),
          undefined,
          payment
        );
        return paymentResult;
      }

      if (!paymentProvider.current) {
        const payment = {
          success: false,
          errorMessage: t("order.errors.payment_provider_init_failed"),
        } as Payment;

        const paymentResult = {
          order,
          payment,
        } as IPaymentResult;

        await addOrderLog(
          order,
          "Falha ao iniciar pagamento",
          undefined,
          payment
        );
        return paymentResult;
      }

      setRechargePayment(recharge);

      try {
        await validateCart(order);


        const orderWithCustomer = await getCustomer(order, kiosk, recharge);

        let orderToPay: IOrder = {
          ...orderWithCustomer,
          superAppEventId: kiosk?.eventId,
          registroSessaoPOSId: kiosk?.registroSessaoPOSId,
          currentSessionId: kiosk?.eventSessionId,
        };

        if (kiosk?.enableCpfDiscount && orderWithCustomer.customer?.document && kiosk?.eventId) {
          const result = await customerDiscountsRef.current?.open(orderToPay, kiosk.eventId);
          orderToPay = result || orderToPay;
        }

        await addOrderLog(
          { ...orderToPay, printType: kiosk.printType, rechargeType: recharge } as IOrder,
          "Pagamento iniciado"
        );

        const result = await paymentProvider.current.open(
          orderToPay,
          isNominalData.current ? TransactionType.cashless : paymentType,
          isNominalData.current ? cardData ?? undefined : undefined
        );

        await onSucessPayment(
          kiosk,
          result.order,
          result.paymentType,
          result.payment,
          recharge,
          result.pedidoPos
        );

        return result;
      } catch (_error) {
        return handlePaymentError(_error, order);
      }
    },
    [
      addOrderLog,
      getCustomer,
      handlePaymentError,
      kiosk,
      onSucessPayment,
      validateCart,
      cardData,
      isNominalData,
      customerDiscountsRef,
      t
    ]
  );

  useImperativeHandle(orderProviderRef, () => ({ startPayment }));

  return {
    startPayment,
    paymentProvider,
    rechargePayment,
    documentToNf,
    onpressKeyboard,
    onPressBack,
    includDocument,
    setIncludDocument,
    docError,
    enableNfAction,
    documentOwnerName,
    documentLookupLoading,
    t
  };
};

export default UseOrderProvider;
